import posthog from 'posthog-js';

// Тонкая обёртка над posthog-js. init() читает env, ставит гарды на DEV/пустой
// ключ (в этих случаях всё становится no-op — dev не пачкает прод-данные,
// сборка без VITE_POSTHOG_KEY не падает). track() типобезопасен через
// AnalyticsEvents map. Ни один вызов не бросает — если posthog не поднят,
// капчурим тихо в никуда.
//
// Ключ и хост НИКОГДА не в коде — только через import.meta.env.VITE_POSTHOG_*.

// Единый источник правды: event name → payload. analyticsBindings.ts
// использует эти же типы для подписки на scene.events эмиты.
export interface AnalyticsEvents {
  session_start: { platform: string };
  session_end: { durationMs: number };
  level_start: { level: number };
  level_complete: { level: number; durationMs: number };
  level_failed: { level: number; killedBy: string; wave: number };
  weapon_purchased: { id: string };
  boss_defeated: { boss?: string };
  game_over: Record<string, never>;
  final_victory: Record<string, never>;
}

export type AnalyticsEvent = keyof AnalyticsEvents;

let initialized = false;
let sessionStartedAt = 0;
let sentSessionEnd = false;

export function init(): void {
  if (initialized) return;

  // (а) DEV: init отключён — консоль и события в PostHog остаются чистыми.
  // (б) Отсутствует ключ или хост в env — тоже no-op (build без ENV не падает).
  if (import.meta.env.DEV) return;

  const key = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST;
  if (!key || !host) return;

  posthog.init(key, {
    api_host: host,
    person_profiles: 'identified_only',
    // Игра, не сайт — pageview не нужен, autocapture кликов забил бы шум.
    capture_pageview: false,
    autocapture: false,
  });
  initialized = true;

  // Telegram: сопоставляем distinct_id с id игрока чтобы события группировались
  // между сессиями и устройствами. Вне TG (обычный браузер) — оставляем аноним
  // distinct_id от PostHog.
  const tgUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
  if (tgUserId !== undefined) {
    posthog.identify(`tg_${tgUserId}`);
  }
}

export function track<E extends AnalyticsEvent>(
  event: E,
  props: AnalyticsEvents[E],
): void {
  if (!initialized) return;
  posthog.capture(event, props);
}

// Отдельная функция для session_start — фиксирует timestamp для последующего
// session_end.durationMs. Вызывается ОДИН раз в main.ts сразу после init().
export function markSessionStart(props: AnalyticsEvents['session_start']): void {
  sessionStartedAt = performance.now();
  sentSessionEnd = false;
  track('session_start', props);
}

// Session end: считаем durationMs от session_start. Отправляем через sendBeacon
// — гарантирует доставку в момент unload/hidden (обычный XHR браузер часто
// режет). Идемпотентно: beforeunload + visibilitychange('hidden') могут
// стрельнуть подряд, флаг sentSessionEnd предотвращает дубль.
export function endSession(): void {
  if (sentSessionEnd) return;
  sentSessionEnd = true;
  if (!initialized) return;
  const durationMs = Math.round(performance.now() - sessionStartedAt);
  posthog.capture(
    'session_end',
    { durationMs },
    { transport: 'sendBeacon' },
  );
}
